import {
  asCampaignStatus,
  asCount,
  asIso,
  asPausedFrom,
  asRecipientStatus,
  asSendMode,
  asStrings,
  asText,
  mapAudienceSnapshot,
  mapRecipientsPage,
  mapSendErrors,
  mapSendPoints,
  type ApiApproveBase,
  type ApiCampaignBase,
  type ApiRecipientBase,
  type ApiRecipientsPage,
} from '@/features/marketing-shared';
import type {
  CampaignDraftInput,
  EmailBlock,
  EmailCampaign,
  EmailTemplate,
  IdentityStatus,
  RecipientRow,
  SendingIdentity,
  TemplateBlockKind,
  TemplateCategory,
  TemplateDraft,
  TemplateEditor,
  TemplateOrigin,
} from '../model/types';

/**
 * API → tela e tela → API do e-mail marketing. O formato do servidor está em
 * `legacy/fixe-api/services/marketingCampaign.js` (`campaignDto`, `templateDto`,
 * `identitiesFor`, `parseApprove`, `saveTemplate`).
 */

export interface ApiEmailMetrics {
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  rejected: number;
  unsubscribed: number;
}

export interface ApiEmailRecipient extends ApiRecipientBase {
  emailMasked?: string;
  device?: string;
}

export interface ApiEmailCampaign extends ApiCampaignBase {
  fromName?: string;
  fromEmail?: string;
  subject?: string;
  previewText?: string;
  templateId?: string;
  templateName?: string;
  tags?: string[];
  /** Sempre `[]` na API: o conteúdo mora no template. Fica pelo editor antigo. */
  blocks?: unknown[];
  metrics?: ApiEmailMetrics;
  recipients?: ApiRecipientsPage<ApiEmailRecipient>;
}

export interface ApiEmailTemplate {
  id: string;
  idPassbook?: string | null;
  name: string;
  category: string;
  description: string;
  subject: string;
  previewText: string;
  editor: string;
  origin: string;
  blocks: unknown[];
  html: string;
  beefreeJson: string;
  plainText: string;
  tags: string[];
  domain: string;
  mailersendTemplateId?: string | null;
  updatedAt: string | null;
}

export interface ApiSendingIdentity {
  id: string;
  domain: string;
  mailersendDomainId?: string;
  fromName: string;
  fromEmail: string;
  status: string;
  detail: string;
}

export interface ApiEmailApproveBody extends ApiApproveBase {
  /** Redundante com `segmentationId`: a API aceita os dois formatos. */
  audience: { audienceId: string };
  fromName: string;
  fromEmail: string;
  subject: string;
  previewText: string;
  templateId: string;
  tags: string[];
}

export interface ApiTemplateSaveBody {
  id?: string;
  name: string;
  category: TemplateCategory;
  description: string;
  subject: string;
  previewText: string;
  editor: TemplateEditor;
  html: string;
  beefreeJson: string;
  plainText: string;
  tags: string[];
  domain: string;
  blocks: EmailBlock[];
}

export const MAX_TEMPLATE_TAGS = 5;

const CATEGORIES: readonly TemplateCategory[] = ['Promoção', 'Relacionamento', 'Aviso'];
const EDITORS: readonly TemplateEditor[] = ['drag-drop', 'html', 'rich-text', 'beefree'];
const BLOCK_KINDS: readonly TemplateBlockKind[] = ['header', 'hero', 'text', 'image', 'button', 'product', 'social', 'divider'];
const IDENTITY_STATUSES: readonly IdentityStatus[] = ['verified', 'pending', 'failed'];

export function asTemplateCategory(value: unknown): TemplateCategory {
  return CATEGORIES.includes(value as TemplateCategory) ? (value as TemplateCategory) : 'Promoção';
}

export function asTemplateEditor(value: unknown): TemplateEditor {
  return EDITORS.includes(value as TemplateEditor) ? (value as TemplateEditor) : 'beefree';
}

function asTemplateOrigin(value: unknown): TemplateOrigin {
  return value === 'sandbox' ? 'sandbox' : 'local';
}

function asIdentityStatus(value: unknown): IdentityStatus {
  return IDENTITY_STATUSES.includes(value as IdentityStatus) ? (value as IdentityStatus) : 'pending';
}

/** A API guarda `blocks` como opaco; aqui só entra o que tem a forma do editor antigo. */
export function asEmailBlocks(value: unknown): EmailBlock[] {
  if (!Array.isArray(value)) return [];
  const blocks: EmailBlock[] = [];
  value.forEach((item, index) => {
    if (!item || typeof item !== 'object') return;
    const record = item as Record<string, unknown>;
    if (!BLOCK_KINDS.includes(record.kind as TemplateBlockKind)) return;
    blocks.push({
      id: asText(record.id) || `blk-${index}`,
      kind: record.kind as TemplateBlockKind,
      label: asText(record.label),
      text: asText(record.text),
    });
  });
  return blocks;
}

function mapEmailMetrics(metrics: ApiEmailMetrics | undefined): EmailCampaign['metrics'] {
  if (!metrics || typeof metrics !== 'object') return undefined;
  return {
    sent: asCount(metrics.sent),
    delivered: asCount(metrics.delivered),
    opened: asCount(metrics.opened),
    clicked: asCount(metrics.clicked),
    rejected: asCount(metrics.rejected),
    unsubscribed: asCount(metrics.unsubscribed),
  };
}

export function mapEmailRecipient(row: ApiEmailRecipient): RecipientRow {
  return {
    id: asText(row.id),
    name: asText(row.name),
    emailMasked: asText(row.emailMasked),
    status: asRecipientStatus(row.status),
    reason: asText(row.reason),
    at: asText(row.at),
    device: asText(row.device) || '—',
  };
}

export function mapEmailCampaign(dto: ApiEmailCampaign): EmailCampaign {
  return {
    id: asText(dto.id),
    name: asText(dto.name),
    fromName: asText(dto.fromName),
    fromEmail: asText(dto.fromEmail),
    subject: asText(dto.subject),
    previewText: asText(dto.previewText),
    status: asCampaignStatus(dto.status),
    templateId: asText(dto.templateId),
    templateName: asText(dto.templateName),
    tags: asStrings(dto.tags, MAX_TEMPLATE_TAGS),
    blocks: asEmailBlocks(dto.blocks),
    audience: mapAudienceSnapshot(dto.audience),
    scheduledAt: asIso(dto.scheduledAt),
    sendMode: asSendMode(dto.sendMode),
    createdAt: asIso(dto.createdAt) ?? '',
    pausedFrom: asPausedFrom(dto.pausedFrom),
    failureSummary: asText(dto.failureSummary) || undefined,
    metrics: mapEmailMetrics(dto.metrics),
    evolution: mapSendPoints(dto.evolution),
    errors: mapSendErrors(dto.errors),
    recipients: mapRecipientsPage(dto.recipients, mapEmailRecipient),
  };
}

export function mapEmailTemplate(dto: ApiEmailTemplate): EmailTemplate {
  return {
    id: asText(dto.id),
    name: asText(dto.name),
    category: asTemplateCategory(dto.category),
    description: asText(dto.description),
    subject: asText(dto.subject),
    previewText: asText(dto.previewText),
    editor: asTemplateEditor(dto.editor),
    origin: asTemplateOrigin(dto.origin),
    blocks: asEmailBlocks(dto.blocks),
    html: asText(dto.html),
    beefreeJson: asText(dto.beefreeJson),
    plainText: asText(dto.plainText),
    tags: asStrings(dto.tags, MAX_TEMPLATE_TAGS),
    domain: asText(dto.domain),
    updatedAt: asIso(dto.updatedAt) ?? '',
  };
}

export function mapSendingIdentity(dto: ApiSendingIdentity): SendingIdentity {
  return {
    id: asText(dto.id) || `${asText(dto.mailersendDomainId)}:${asText(dto.fromEmail)}`,
    domain: asText(dto.domain),
    fromName: asText(dto.fromName),
    fromEmail: asText(dto.fromEmail),
    status: asIdentityStatus(dto.status),
    detail: asText(dto.detail),
  };
}

function cleanTags(tags: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of tags) {
    const tag = raw.trim();
    if (!tag || seen.has(tag)) continue;
    seen.add(tag);
    out.push(tag);
    if (out.length >= MAX_TEMPLATE_TAGS) break;
  }
  return out;
}

/** Corpo de `POST /:idPassbook/campaigns/approve` do e-mail (`parseApprove`). */
export function toEmailApproveBody(input: CampaignDraftInput, allowPartial: boolean): ApiEmailApproveBody {
  const audienceId = input.audience.audienceId;
  return {
    ...(input.id ? { id: input.id } : {}),
    name: input.name.trim(),
    segmentationId: audienceId,
    audience: { audienceId },
    sendMode: input.sendMode,
    scheduledAt: input.sendMode === 'later' ? input.scheduledAt : null,
    allowPartial,
    fromName: input.fromName.trim(),
    fromEmail: input.fromEmail.trim().toLowerCase(),
    subject: input.subject.trim(),
    previewText: input.previewText.trim(),
    templateId: input.templateId,
    tags: cleanTags(input.tags),
  };
}

/** Corpo de `PUT /:idPassbook/templates`. O rascunho já vem normalizado (`prepareTemplateDraft`). */
export function toTemplateSaveBody(draft: TemplateDraft): ApiTemplateSaveBody {
  return {
    ...(draft.id ? { id: draft.id } : {}),
    name: draft.name.trim().slice(0, 50),
    category: draft.category,
    description: draft.description.trim(),
    subject: draft.subject.trim(),
    previewText: draft.previewText.trim(),
    editor: draft.editor,
    html: draft.html,
    beefreeJson: draft.beefreeJson,
    plainText: draft.plainText,
    tags: cleanTags(draft.tags),
    domain: draft.domain.trim().toLowerCase(),
    blocks: draft.blocks.map((block) => ({ ...block })),
  };
}
